import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, Card, Spinner, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSync,
  faFilter,
  faArrowUp,
  faArrowDown,
  faMinus
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import moment from 'moment';
// import { ChartFactory } from '../../../AIDashboard/AIDashboard/utils';
import { ChartFactory } from '../../../ChartUtility/charts';
import { useGraphData } from '../hooks/useMeters';
import { buildWidgetConfig } from '../services/meterApi';
import '../styles/MetersDashboard.css';

const Company362MeterDashboard = ({ 
  financialYearOptions,
  sourceOptions,
  onRefresh 
}) => {
  const [lastRefreshTime, setLastRefreshTime] = useState(moment());

  // Global Filters
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(financialYearOptions?.[0]?.value);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSubLocation, setSelectedSubLocation] = useState(null);

  // Get sub-locations based on selected location
  const subLocationOptions = useMemo(() => {
    if (!selectedLocation) return [];
    const location = sourceOptions.find(s => s.value === selectedLocation.value);
    return location?.subLocations || [];
  }, [selectedLocation, sourceOptions]);


  useEffect(() => {
    if (financialYearOptions) {
      setSelectedFinancialYear(financialYearOptions?.[0]?.value);
    }
  }, [financialYearOptions]);

  const handleManualRefresh = () => {
    onRefresh?.();
    setLastRefreshTime(moment());
  };

  // Summary metrics configuration
  const summaryMetrics = [
    { key: 'groundWaterWithdrawal', title: 'Ground Water Withdrawal', module: 'Water', category: 'Withdrawal', subCategory: 'Ground Water', kpi: 'Borewell' },
    { key: 'groundWaterConsumption', title: 'Ground Water Consumption', module: 'Water', category: 'Consumption', subCategory: 'Ground Water', kpi: 'From Borewell to Admin' },
    { key: 'tankerWaterConsumption', title: 'Third Party Water Withdrawal/Consumption', module: 'Water', category: 'Consumption', subCategory: 'Third Party Tanker Water' },
    { key: 'untreatedWaterETP', title: 'Untreated Water (ETP Inlet)', module: 'Water', category: 'Untreated Water', kpi: 'ETP (Untreated)' },
    { key: 'treatedWaterETP', title: 'Treated Water (ETP Outlet)', module: 'Water', category: 'Treated Water', subCategory: 'Recycled', kpi: 'ETP' },
    { key: 'untreatedWaterSTP', title: 'Untreated Water (STP Inlet)', module: 'Water', category: 'Untreated Water', kpi: 'STP (Untreated)' },
    { key: 'treatedWaterSTP', title: 'Treated Water (STP Outlet)', module: 'Water', category: 'Treated Water', subCategory: 'Recycled', kpi: 'STP' },
    { key: 'treatedWater', title: 'Final Treated Water (STP + ETP)', module: 'Water', category: 'Treated Water', subCategory: 'Recycled', kpi: 'Final Treated (STP + ETP)' },
  ];

  return (
    <div className="meters-dashboard">
      {/* Header with Refresh */}
      <div className="dashboard-header mb-4">
        <h2>Water Management Dashboard</h2>
        <div className="header-controls">
          <Button 
            variant="link" 
            className="refresh-icon-btn"
            onClick={handleManualRefresh}
            title="Refresh dashboard"
          >
            <FontAwesomeIcon icon={faSync} />
          </Button>
          <span className="last-refresh">
            Last updated: {lastRefreshTime.format('HH:mm:ss')}
          </span>
        </div>
      </div>

      {/* Global Filters */}
      <Card className="mb-4">
        <Card.Header>
          <h5>
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Global Filters
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Financial Year</Form.Label>
                <Select
                  options={financialYearOptions}
                  value={financialYearOptions.find(opt => opt.value === selectedFinancialYear)}
                  onChange={(selected) => setSelectedFinancialYear(selected?.value)}
                  placeholder="Select Financial Year"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                />
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group>
                <Form.Label>Location</Form.Label>
                <Select
                  options={sourceOptions}
                  value={selectedLocation}
                  onChange={(selected) => {
                    setSelectedLocation(selected);
                    setSelectedSubLocation(null);
                  }}
                  placeholder="Select Location"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed" 
                  isClearable 
                />
              </Form.Group>
            </Col>
            
            <Col md={4}>
              <Form.Group>
                <Form.Label>Sub Location</Form.Label>
                <Select
                  options={subLocationOptions}
                  value={selectedSubLocation}
                  onChange={setSelectedSubLocation}
                  placeholder="Select Sub Location"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  isDisabled={!selectedLocation}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Summary Section */}
      <div className="summary-section mb-4">
        <h3 className="section-title">Today's Summary</h3>
        <Row>
          {selectedFinancialYear && summaryMetrics.map((metric, index) => (
            <Col md={4} key={index} className="mb-3">
              {metric.key === 'treatedWaterSTP' && (
                <StpTreatedSummaryCard
                  metric={metric}
                  currentFY={selectedFinancialYear}
                  locationId={selectedLocation?.value}
                  subLocationId={selectedSubLocation?.value}
                />
              )}
              {metric.key !== 'treatedWaterSTP' && (
                <SummaryCard
                  metric={metric}
                  currentFY={selectedFinancialYear}
                  locationId={selectedLocation?.value}
                  subLocationId={selectedSubLocation?.value}
                />
              )}
            </Col>
          ))}
        </Row>
      </div>

      {/* Graphs Section */}
      <div className="graphs-section">
        <h3 className="section-title">Detailed Analysis</h3>

        {/* a. Total Ground Water Withdrawal */}
        <Company362Graph
          title="Total Ground Water Withdrawal"
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
          module="Water"
          category="Withdrawal"
          subCategory="Ground Water"
          kpi="Borewell"
          stackByField={null}
        />

        {/* b. Total Ground Water Withdrawal By Borewell */}
        <Company362Graph
          title="Borewell-wise Groundwater Withdrawal"
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
          module="Water"
          category="Withdrawal"
          subCategory="Ground Water"
          kpi="Borewell"
          stackByField="meter_id"
        />

        {/* c. Total Ground Water Consumption */}
        <Company362Graph
          title="Total Ground Water Consumption (From Borewell to Admin Top)"
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
          module="Water"
          category="Consumption"
          subCategory="Ground Water"
          kpi="From Borewell to Admin"
          stackByField={null}
        />

        {/* d. Total Ground Water Consumption By Unit */}
        <GroundWaterConsumptionByLocation
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
        />

        <CanteenWaterConsumption
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
        />

        {/* <Company362Graph
          title="Meter-wise Canteen Water Consumption"
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
          module="Water"
          kpi={['Canteen + GES', 'Awanthi 50 ppm', 'GES']}
          stackByField="kpi"
        /> */}

        {/* e. Total Tanker Water Consumption */}
        <Company362Graph
          title="Total Tanker Water Withdrawal/Consumption"
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
          module="Water"
          category="Consumption"
          subCategory="Third Party Tanker Water"
          stackByField={null}
        />

        {/* f. Total Tanker Water Consumption By KPI */}
        <Company362Graph
          title="Total Tanker Water Withdrawal/Consumption By KPI"
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
          module="Water"
          category="Consumption"
          subCategory="Third Party Tanker Water"
          stackByField="kpi"
        />

        {/* g. Total Treated Water */}
        <Company362Graph
          title="Total Treated Water"
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
          module="Water"
          category="Treated Water"
          subCategory="Recycled"
          kpi="Final Treated (STP + ETP)"
          stackByField={null}
        />

        <TreatmentWiseStpWater
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
        />

        <Company362Graph
          title="Treatment-wise ETP Water "
          currentFY={selectedFinancialYear}
          locationId={selectedLocation?.value}
          subLocationId={selectedSubLocation?.value}
          module="Water"
          kpi={['ETP (Untreated)', 'ETP']}
          stackByField="kpi"
        />
      </div>
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ metric, currentFY, locationId, subLocationId }) => {
  // Build config to fetch today's and yesterday's data in single call
  const summaryConfig = useMemo(() => {
    const config = {
      financialYear: currentFY,
      startDateTime: moment().subtract(2, 'days').startOf('day').toISOString(), // Yesterday 00:00:00
      endDateTime: moment().toISOString(), // Now
      locationId: locationId?.toString() || '1',
      title: metric.title,
      module: metric.module,
      category: metric.category,
      subCategory: metric.subCategory,
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: 'days',
      xAxisField: 'displayPeriods'
    };

    if (subLocationId) config.subLocationId = subLocationId?.toString();
    if (metric.kpi) config.kpi = metric.kpi;

    return buildWidgetConfig(config);
  }, [metric, currentFY, locationId, subLocationId]);

  const { data, isLoading, error } = useGraphData(
    summaryConfig, 
    { 
      enabled: !!summaryConfig && !!currentFY
    }
  );

  const stats = useMemo(() => {
    if (!data?.data) return null;

    const dataArray = Array.isArray(data.data) ? data.data : data.data.data;
    if (!dataArray) return null;

    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');

    // Find today's and yesterday's data by matching displayPeriods
    const todayData = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(today, 'day')
    );
    
    const yesterdayData = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(yesterday, 'day')
    );
    
    const todayValue = todayData ? Number(todayData.sum_value || 0) : 0;
    const yesterdayValue = yesterdayData ? Number(yesterdayData.sum_value || 0) : 0;
    const change = yesterdayValue ? ((todayValue - yesterdayValue) / yesterdayValue) * 100 : 0;
    const changeValue = todayValue - yesterdayValue;

    return { 
      todayValue, 
      yesterdayValue, 
      change, 
      changeValue 
    };
  }, [data]);

  if (isLoading) {
    return (
      <Card className="kpi-card">
        <Card.Body>
          <h6 className="kpi-card-title">{metric.title}</h6>
          <div className="text-center">
            <Spinner animation="border" size="sm" variant="primary" />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!stats || error) {
    return (
      <Card className="kpi-card">
        <Card.Body>
          <h6 className="kpi-card-title">{metric.title}</h6>
          <div className="text-center">
            <small className="text-muted">No data available</small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="kpi-card">
      <Card.Body>
        <h6 className="kpi-card-title">{metric.title}</h6>
        <p className="kpi-card-subtitle">Today</p>
        <div className="kpi-value">
          {stats.todayValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          <span className="kpi-unit">m³</span>
        </div>
        <div className={`kpi-change ${stats.change > 0 ? 'positive' : stats.change < 0 ? 'negative' : 'neutral'}`}>
          <FontAwesomeIcon icon={stats.change > 0 ? faArrowUp : stats.change < 0 ? faArrowDown : faMinus} className="me-1" />
          <span>{Math.abs(stats.change).toFixed(1)}%</span>
          <span className="change-value ms-2">
            ({stats.change > 0 ? '+' : ''}{stats.changeValue.toFixed(2)} m³)
          </span>
        </div>
        <small className="text-muted">vs Yesterday</small>
        <div className="mt-2">
          <small className="text-muted">Yesterday: {stats.yesterdayValue.toFixed(2)} m³</small>
        </div>
      </Card.Body>
    </Card>
  );
};

const StpTreatedSummaryCard = ({ currentFY, locationId, subLocationId }) => {
  // Build config to fetch both KPIs in one call
  const summaryConfig = useMemo(() => {
    const config = {
      financialYear: currentFY,
      startDateTime: moment().subtract(2, 'days').startOf('day').toISOString(),
      endDateTime: moment().toISOString(),
      locationId: locationId?.toString() || '1',
      module: 'Water',
      kpi: ['ETP', 'Final Treated (STP + ETP)'],
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: 'days',
      xAxisField: 'displayPeriods'
    };

    if (subLocationId) config.subLocationId = subLocationId?.toString();

    return buildWidgetConfig(config);
  }, [currentFY, locationId, subLocationId]);

  const { data, isLoading, error } = useGraphData(summaryConfig, {
    enabled: !!summaryConfig && !!currentFY
  });

  const stats = useMemo(() => {
    if (!data?.data?.data) return null;

    const rows = data.data.data;

    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');

    const byDay = {};

    rows.forEach(r => {
      if (!r.period_ts) return;
      const day = moment(r.period_ts).startOf('day').format('YYYY-MM-DD');
      if (!byDay[day]) byDay[day] = {};
      byDay[day][r.kpi] = Number(r.sum_value) || 0;
    });

    const todayKey = today.format('YYYY-MM-DD');
    const yesterdayKey = yesterday.format('YYYY-MM-DD');

    const todayFinal = byDay[todayKey]?.['Final Treated (STP + ETP)'] || 0;
    const todayETP = byDay[todayKey]?.['ETP'] || 0;
    const todayValue = Math.max(0, todayFinal - todayETP);

    const yFinal = byDay[yesterdayKey]?.['Final Treated (STP + ETP)'] || 0;
    const yETP = byDay[yesterdayKey]?.['ETP'] || 0;
    const yesterdayValue = Math.max(0, yFinal - yETP);

    const changeValue = todayValue - yesterdayValue;
    const change = yesterdayValue
      ? (changeValue / yesterdayValue) * 100
      : 0;

    return {
      todayValue,
      yesterdayValue,
      change,
      changeValue
    };
  }, [data]);

  // ---------------- RENDER STATES ----------------

  if (isLoading) {
    return (
      <Card className="kpi-card">
        <Card.Body>
          <h6 className="kpi-card-title">STP Treated Water</h6>
          <div className="text-center">
            <Spinner animation="border" size="sm" variant="primary" />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!stats || error) {
    return (
      <Card className="kpi-card">
        <Card.Body>
          <h6 className="kpi-card-title">STP Treated Water</h6>
          <div className="text-center">
            <small className="text-muted">No data available</small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="kpi-card">
      <Card.Body>
        <h6 className="kpi-card-title">STP Treated Water</h6>
        <p className="kpi-card-subtitle">Today</p>

        <div className="kpi-value">
          {stats.todayValue.toLocaleString(undefined, {
            maximumFractionDigits: 2
          })}
          <span className="kpi-unit">m³</span>
        </div>

        <div
          className={`kpi-change ${
            stats.change > 0
              ? 'positive'
              : stats.change < 0
              ? 'negative'
              : 'neutral'
          }`}
        >
          <FontAwesomeIcon
            icon={
              stats.change > 0
                ? faArrowUp
                : stats.change < 0
                ? faArrowDown
                : faMinus
            }
            className="me-1"
          />
          <span>{Math.abs(stats.change).toFixed(1)}%</span>
          <span className="change-value ms-2">
            ({stats.change > 0 ? '+' : ''}
            {stats.changeValue.toFixed(2)} m³)
          </span>
        </div>

        <small className="text-muted">vs Yesterday</small>

        <div className="mt-2">
          <small className="text-muted">
            Yesterday: {stats.yesterdayValue.toFixed(2)} m³
          </small>
        </div>

        <div className="mt-2">
          <small className="text-muted">
            Calculated as: Final Treated (STP + ETP) − ETP
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};


// Company 362 Graph Component with local filters
const Company362Graph = ({ 
  title, 
  currentFY, 
  locationId, 
  subLocationId,
  module,
  category, 
  subCategory, 
  kpi,
  stackByField 
}) => {
  // Local filters
  const [timeRange, setTimeRange] = useState('week');
  const [aggregation, setAggregation] = useState('day');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [debouncedConfig, setDebouncedConfig] = useState(null);
  const debounceTimer = React.useRef(null);

  const lastChanged = React.useRef(null);
  const isInitialMount = React.useRef(true);

  // Sync time range and aggregation
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (lastChanged.current === 'timeRange') {
      const defaultAggregations = {
        'hour': 'minute',
        'day': 'hour',
        'week': 'day',
        'month': 'day',
        'custom': 'hour'
      };
      setAggregation(defaultAggregations[timeRange]);
    }
  }, [timeRange]);

  useEffect(() => {
    if (isInitialMount.current) return;

    if (lastChanged.current === 'aggregation') {
      const defaultTimeRanges = {
        'minute': 'hour',
        'hour': 'day',
        'day': 'week',
        'month': 'month'
      };
      const newTimeRange = defaultTimeRanges[aggregation];
      if (newTimeRange && timeRange !== 'custom') {
        setTimeRange(newTimeRange);
      }
    }
  }, [aggregation, timeRange]);

  const handleTimeRangeChange = (e) => {
    lastChanged.current = 'timeRange';
    setTimeRange(e.target.value);
  };

  const handleAggregationChange = (e) => {
    lastChanged.current = 'aggregation';
    setAggregation(e.target.value);
  };

  // Calculate date range
  const dateRange = useMemo(() => {
    const now = moment();
    switch (timeRange) {
      case 'hour':
        return { start: moment().subtract(1, 'hour'), end: now };
      case 'day':
        return { start: moment().subtract(1, 'day'), end: now };
      case 'week':
        return { start: moment().subtract(7, 'days'), end: now };
      case 'month':
        return { start: moment().subtract(1, 'month'), end: now };
      case 'custom':
        return {
          start: customStart || moment().subtract(7, 'days'),
          end: customEnd || now
        };
      default:
        return { start: moment().subtract(7, 'days'), end: now };
    }
  }, [timeRange, customStart, customEnd]);

  // Build graph config
  const graphConfig = useMemo(() => {
    const config = {
      financialYear: currentFY,
      startDateTime: dateRange.start.toISOString(),
      endDateTime: dateRange.end.toISOString(),
      locationId: locationId?.toString() || '1',
      title,
      module,
      category,
      subCategory,
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: aggregation === 'minute' ? 'minutes' : aggregation === 'hour' ? 'hours' : aggregation === 'day' ? 'days' : 'months',
      xAxisField: 'displayPeriods',
      chartType: 'line'
    };

    if (subLocationId) config.subLocationId = subLocationId?.toString();
    if (kpi) config.kpi = kpi;
    if (stackByField) {
      config.enableStacking = true;
      config.stackByField = stackByField;
    }

    return buildWidgetConfig(config);
  }, [currentFY, dateRange, locationId, subLocationId, category, subCategory, kpi, aggregation, stackByField]);


    // Debounce config changes to prevent API calls during filter transitions
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedConfig(graphConfig);
    }, 300); // 300ms delay

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [graphConfig]);

    const { data: graphData, isLoading } = useGraphData(
      debouncedConfig, 
      { 
        enabled: !!debouncedConfig && !!currentFY
      }
    );

  return (
    <Card className="graph-card mb-4">
      <Card.Header>
        <h5 className="mb-0">{title}</h5>
      </Card.Header>
      <Card.Body>
        {/* Local Filters */}
        <div className="graph-controls mb-3">
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Time Range</Form.Label>
                <Form.Select size="sm" value={timeRange} onChange={handleTimeRangeChange}>
                  <option value="hour">Last Hour</option>
                  <option value="day">Last 24 Hours</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {timeRange === 'custom' && (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Start Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customStart ? customStart.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomStart(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">End Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customEnd ? customEnd.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomEnd(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Aggregation</Form.Label>
                <Form.Select size="sm" value={aggregation} onChange={handleAggregationChange}>
                  <option value="minute">Per Minute</option>
                  <option value="hour">Hourly</option>
                  <option value="day">Daily</option>
                  <option value="month">Monthly</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Graph */}
        <div className="graph-container">
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <ChartFactory
              chartData={{
                data: graphData?.data || [],
                chartType: 'line',
                title: title
              }}
              chartOptions={{
                height: 350,
                stacked: !!stackByField,
                transform: true
              }}
              group_by={['displayPeriods',stackByField]}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

// Special component for Ground Water Consumption By Unit with Production Unit calculation
const GroundWaterConsumptionByLocation = ({ currentFY, locationId, subLocationId }) => {
  // Local filters
  const [timeRange, setTimeRange] = useState('week');
  const [aggregation, setAggregation] = useState('day');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [debouncedConfig, setDebouncedConfig] = useState(null);

  const lastChanged = React.useRef(null);
  const isInitialMount = React.useRef(true);
  const debounceTimer = React.useRef(null); 

  // Sync time range and aggregation
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (lastChanged.current === 'timeRange') {
      const defaultAggregations = {
        'hour': 'minute',
        'day': 'hour',
        'week': 'day',
        'month': 'day',
        'custom': 'hour'
      };
      setAggregation(defaultAggregations[timeRange]);
    }
  }, [timeRange]);

  useEffect(() => {
    if (isInitialMount.current) return;

    if (lastChanged.current === 'aggregation') {
      const defaultTimeRanges = {
        'minute': 'hour',
        'hour': 'day',
        'day': 'week',
        'month': 'month'
      };
      const newTimeRange = defaultTimeRanges[aggregation];
      if (newTimeRange && timeRange !== 'custom') {
        setTimeRange(newTimeRange);
      }
    }
  }, [aggregation, timeRange]);

  const handleTimeRangeChange = (e) => {
    lastChanged.current = 'timeRange';
    setTimeRange(e.target.value);
  };

  const handleAggregationChange = (e) => {
    lastChanged.current = 'aggregation';
    setAggregation(e.target.value);
  };

  // Calculate date range
  const dateRange = useMemo(() => {
    const now = moment();
    switch (timeRange) {
      case 'hour':
        return { start: moment().subtract(1, 'hour'), end: now };
      case 'day':
        return { start: moment().subtract(1, 'day'), end: now };
      case 'week':
        return { start: moment().subtract(7, 'days'), end: now };
      case 'month':
        return { start: moment().subtract(1, 'month'), end: now };
      case 'custom':
        return {
          start: customStart || moment().subtract(7, 'days'),
          end: customEnd || now
        };
      default:
        return { start: moment().subtract(7, 'days'), end: now };
    }
  }, [timeRange, customStart, customEnd]);

  // Build graph config - stack by KPI to get all units
  const graphConfig = useMemo(() => {
    const config = {
      financialYear: currentFY,
      startDateTime: dateRange.start.toISOString(),
      endDateTime: dateRange.end.toISOString(),
      locationId: locationId?.toString() || '1',
      module: 'Water',
      category: 'Consumption',
      subCategory: 'Ground Water',
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: aggregation === 'minute' ? 'minutes' : aggregation === 'hour' ? 'hours' : aggregation === 'day' ? 'days' : 'months',
      xAxisField: 'displayPeriods',
      stackByField: 'kpi',
      chartType: 'line',
      enableStacking: true
    };

    if (subLocationId) config.subLocationId = subLocationId?.toString();

    return buildWidgetConfig(config);
  }, [currentFY, dateRange, locationId, subLocationId, aggregation]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedConfig(graphConfig);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [graphConfig]);

  const { data: rawData, isLoading } = useGraphData(
    debouncedConfig, 
    { 
      enabled: !!debouncedConfig && !!currentFY
    }
  );

  // Process data to calculate Production Unit
  const processedData = useMemo(() => {
    if (!rawData?.data) return [];

    const dataArray = rawData.data;
    if (!dataArray) return [];

    // Group by displayPeriods
    const groupedByPeriod = {};
    dataArray.forEach(item => {
      const period = item.displayPeriods;
      if (!groupedByPeriod[period]) {
        groupedByPeriod[period] = {};
      }
      groupedByPeriod[period][item.kpi] = {
        sum_value: Number(item.sum_value) || 0,
        unit: item.unit
      }
    });

    // Calculate Production Unit for each period
    const result = [];
    Object.keys(groupedByPeriod).forEach(period => {
      const periodData = groupedByPeriod[period];
      
      // Get Admin Top value
      const adminTop = periodData['From Borewell to Admin'].sum_value || 0;
      
      // Calculate Production Unit by subtracting all other units from Admin Top
      let productionUnit = adminTop;
      Object.keys(periodData).forEach(kpi => {
        if (kpi !== 'From Borewell to Admin' && kpi !== 'Canteen + GES') {
          productionUnit -= periodData[kpi].sum_value;
        }
      });

      // Add all original KPIs
      Object.keys(periodData).forEach(kpi => {
        if (kpi === 'From Borewell to Admin' || kpi === 'Canteen + GES') return;
        result.push({
          displayPeriods: period,
          kpi: kpi,
          sum_value: periodData[kpi].sum_value,
          unit: periodData[kpi].unit
        });
      });

      // Add Production Unit
      result.push({
        displayPeriods: period,
        kpi: 'KIL',
        sum_value: Math.max(0, productionUnit),
        unit: periodData['From Borewell to Admin'].unit
      });
    });

    return result;
  }, [rawData]);

  return (
    <Card className="graph-card mb-4">
      <Card.Header>
        <h5 className="mb-0">Location-wise Borewell Water Consumption</h5>
        <small className="text-muted">KIL = From Borewell to Admin - (GES + Sports Club)</small>
      </Card.Header>
      <Card.Body>
        {/* Local Filters */}
        <div className="graph-controls mb-3">
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Time Range</Form.Label>
                <Form.Select size="sm" value={timeRange} onChange={handleTimeRangeChange}>
                  <option value="hour">Last Hour</option>
                  <option value="day">Last 24 Hours</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {timeRange === 'custom' && (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Start Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customStart ? customStart.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomStart(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">End Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customEnd ? customEnd.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomEnd(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Aggregation</Form.Label>
                <Form.Select size="sm" value={aggregation} onChange={handleAggregationChange}>
                  <option value="minute">Per Minute</option>
                  <option value="hour">Hourly</option>
                  <option value="day">Daily</option>
                  <option value="month">Monthly</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="graph-container">
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <ChartFactory
              chartData={{
                data: processedData,
                chartType: 'line',
                title: 'Location-wise Borewell Water Consumption'
              }}
              chartOptions={{
                height: 350,
                stacked: true,
                transform: true
              }}
              group_by={['displayPeriods','kpi']}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

const CanteenWaterConsumption = ({ currentFY, locationId, subLocationId }) => {
  // Local filters
  const [timeRange, setTimeRange] = useState('week');
  const [aggregation, setAggregation] = useState('day');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [debouncedConfig, setDebouncedConfig] = useState(null);

  const lastChanged = React.useRef(null);
  const isInitialMount = React.useRef(true);
  const debounceTimer = React.useRef(null); 

  // Sync time range and aggregation
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (lastChanged.current === 'timeRange') {
      const defaultAggregations = {
        'hour': 'minute',
        'day': 'hour',
        'week': 'day',
        'month': 'day',
        'custom': 'hour'
      };
      setAggregation(defaultAggregations[timeRange]);
    }
  }, [timeRange]);

  useEffect(() => {
    if (isInitialMount.current) return;

    if (lastChanged.current === 'aggregation') {
      const defaultTimeRanges = {
        'minute': 'hour',
        'hour': 'day',
        'day': 'week',
        'month': 'month'
      };
      const newTimeRange = defaultTimeRanges[aggregation];
      if (newTimeRange && timeRange !== 'custom') {
        setTimeRange(newTimeRange);
      }
    }
  }, [aggregation, timeRange]);

  const handleTimeRangeChange = (e) => {
    lastChanged.current = 'timeRange';
    setTimeRange(e.target.value);
  };

  const handleAggregationChange = (e) => {
    lastChanged.current = 'aggregation';
    setAggregation(e.target.value);
  };

  // Calculate date range
  const dateRange = useMemo(() => {
    const now = moment();
    switch (timeRange) {
      case 'hour':
        return { start: moment().subtract(1, 'hour'), end: now };
      case 'day':
        return { start: moment().subtract(1, 'day'), end: now };
      case 'week':
        return { start: moment().subtract(7, 'days'), end: now };
      case 'month':
        return { start: moment().subtract(1, 'month'), end: now };
      case 'custom':
        return {
          start: customStart || moment().subtract(7, 'days'),
          end: customEnd || now
        };
      default:
        return { start: moment().subtract(7, 'days'), end: now };
    }
  }, [timeRange, customStart, customEnd]);

  // Build graph config - stack by KPI to get all units
  const graphConfig = useMemo(() => {
    const config = {
      financialYear: currentFY,
      startDateTime: dateRange.start.toISOString(),
      endDateTime: dateRange.end.toISOString(),
      locationId: locationId?.toString() || '1',
      module: 'Water',
      kpi: ['Canteen + GES', 'Awanthi 50 ppm', 'GES'],
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: aggregation === 'minute' ? 'minutes' : aggregation === 'hour' ? 'hours' : aggregation === 'day' ? 'days' : 'months',
      xAxisField: 'displayPeriods',
      stackByField: 'kpi',
      chartType: 'line',
      enableStacking: true
    };

    if (subLocationId) config.subLocationId = subLocationId?.toString();

    return buildWidgetConfig(config);
  }, [currentFY, dateRange, locationId, subLocationId, aggregation]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedConfig(graphConfig);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [graphConfig]);

  const { data: rawData, isLoading } = useGraphData(
    debouncedConfig, 
    { 
      enabled: !!debouncedConfig && !!currentFY
    }
  );

  // Process data to calculate Production Unit
  const totalCanteenData = useMemo(() => {
    if (!rawData?.data) {
      return null;
    }

    const rows = rawData.data;

    // group by period
    const byPeriod = {};
    rows.forEach(r => {
      if (!byPeriod[r.displayPeriods]) {
        byPeriod[r.displayPeriods] = {};
      }
      byPeriod[r.displayPeriods][r.kpi] = Number(r.sum_value) || 0;
    });

    const totalCanteen = [];

    Object.entries(byPeriod).forEach(([period, kpis]) => {
      const canteenGES = kpis['Canteen + GES'] || 0;
      const awanthi = kpis['Awanthi 50 ppm'] || 0;
      const ges = kpis['GES'] || 0;

      // -------- Graph 1: Total Canteen --------
      totalCanteen.push({
        displayPeriods: period,
        kpi: 'Total Canteen',
        sum_value: Math.max(0, canteenGES + awanthi - ges),
        unit: rows[0]?.unit
      });
    });

    return totalCanteen;
  }, [rawData]);

  return (
    <Card className="graph-card mb-4">
      <Card.Header>
        <h5 className="mb-0">Total Water Consumption – Canteen</h5>
        <small className="text-muted">Canteen Consumption = 11 Canteen + Awanthi 50 ppm - GES</small>
      </Card.Header>
      <Card.Body>
        {/* Local Filters */}
        <div className="graph-controls mb-3">
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Time Range</Form.Label>
                <Form.Select size="sm" value={timeRange} onChange={handleTimeRangeChange}>
                  <option value="hour">Last Hour</option>
                  <option value="day">Last 24 Hours</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {timeRange === 'custom' && (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Start Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customStart ? customStart.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomStart(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">End Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customEnd ? customEnd.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomEnd(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Aggregation</Form.Label>
                <Form.Select size="sm" value={aggregation} onChange={handleAggregationChange}>
                  <option value="minute">Per Minute</option>
                  <option value="hour">Hourly</option>
                  <option value="day">Daily</option>
                  <option value="month">Monthly</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="graph-container">
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <ChartFactory
              chartData={{
                data: totalCanteenData,
                chartType: 'line',
                title: 'Total Water Consumption – Canteen'
              }}
              chartOptions={{
                height: 350,
                stacked: true,
                transform: true
              }}
              group_by={['displayPeriods','kpi',]}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};


const TreatmentWiseStpWater = ({ currentFY, locationId, subLocationId }) => {
  // Local filters
  const [timeRange, setTimeRange] = useState('week');
  const [aggregation, setAggregation] = useState('day');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [debouncedConfig, setDebouncedConfig] = useState(null);

  const lastChanged = React.useRef(null);
  const isInitialMount = React.useRef(true);
  const debounceTimer = React.useRef(null); 

  // Sync time range and aggregation
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (lastChanged.current === 'timeRange') {
      const defaultAggregations = {
        'hour': 'minute',
        'day': 'hour',
        'week': 'day',
        'month': 'day',
        'custom': 'hour'
      };
      setAggregation(defaultAggregations[timeRange]);
    }
  }, [timeRange]);

  useEffect(() => {
    if (isInitialMount.current) return;

    if (lastChanged.current === 'aggregation') {
      const defaultTimeRanges = {
        'minute': 'hour',
        'hour': 'day',
        'day': 'week',
        'month': 'month'
      };
      const newTimeRange = defaultTimeRanges[aggregation];
      if (newTimeRange && timeRange !== 'custom') {
        setTimeRange(newTimeRange);
      }
    }
  }, [aggregation, timeRange]);

  const handleTimeRangeChange = (e) => {
    lastChanged.current = 'timeRange';
    setTimeRange(e.target.value);
  };

  const handleAggregationChange = (e) => {
    lastChanged.current = 'aggregation';
    setAggregation(e.target.value);
  };

  // Calculate date range
  const dateRange = useMemo(() => {
    const now = moment();
    switch (timeRange) {
      case 'hour':
        return { start: moment().subtract(1, 'hour'), end: now };
      case 'day':
        return { start: moment().subtract(1, 'day'), end: now };
      case 'week':
        return { start: moment().subtract(7, 'days'), end: now };
      case 'month':
        return { start: moment().subtract(1, 'month'), end: now };
      case 'custom':
        return {
          start: customStart || moment().subtract(7, 'days'),
          end: customEnd || now
        };
      default:
        return { start: moment().subtract(7, 'days'), end: now };
    }
  }, [timeRange, customStart, customEnd]);

  // Build graph config - stack by KPI to get all units
  const graphConfig = useMemo(() => {
    const config = {
      financialYear: currentFY,
      startDateTime: dateRange.start.toISOString(),
      endDateTime: dateRange.end.toISOString(),
      locationId: locationId?.toString() || '1',
      module: 'Water',
      kpi: ['STP (Untreated)', 'ETP', 'Final Treated (STP + ETP)'],
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: aggregation === 'minute' ? 'minutes' : aggregation === 'hour' ? 'hours' : aggregation === 'day' ? 'days' : 'months',
      xAxisField: 'displayPeriods',
      stackByField: 'kpi',
      chartType: 'line',
      enableStacking: true
    };

    if (subLocationId) config.subLocationId = subLocationId?.toString();

    return buildWidgetConfig(config);
  }, [currentFY, dateRange, locationId, subLocationId, aggregation]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedConfig(graphConfig);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [graphConfig]);

  const { data: rawData, isLoading } = useGraphData(
    debouncedConfig, 
    { 
      enabled: !!debouncedConfig && !!currentFY
    }
  );

  // Process data to calculate Production Unit
  const treatmentWiseStpData = useMemo(() => {
    if (!rawData?.data) {
      return null;
    }

    const rows = rawData.data;

    // group by period
    const byPeriod = {};
    rows.forEach(r => {
      if (!byPeriod[r.displayPeriods]) {
        byPeriod[r.displayPeriods] = {};
      }
      byPeriod[r.displayPeriods][r.kpi] = Number(r.sum_value) || 0;
    });

    const stpBreakdown = [];

    Object.entries(byPeriod).forEach(([period, kpis]) => {
      const etpOutlet = kpis['ETP'] || 0;
      const finalTreated = kpis['Final Treated (STP + ETP)'] || 0;
      const stpInlet = kpis['STP (Untreated)'] || 0;

      stpBreakdown.push({
        displayPeriods: period,
        kpi: 'STP (Untreated)',
        sum_value: Math.max(0, finalTreated - etpOutlet),
        unit: rows[0]?.unit
      });

      stpBreakdown.push({
        displayPeriods: period,
        kpi: 'STP',
        sum_value: stpInlet,
        unit: rows[0]?.unit
      });
    });

    return stpBreakdown;
  }, [rawData]);

  return (
    <Card className="graph-card mb-4">
      <Card.Header>
        <h5 className="mb-0">Treatment-wise STP Water</h5>
      </Card.Header>
      <Card.Body>
        {/* Local Filters */}
        <div className="graph-controls mb-3">
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Time Range</Form.Label>
                <Form.Select size="sm" value={timeRange} onChange={handleTimeRangeChange}>
                  <option value="hour">Last Hour</option>
                  <option value="day">Last 24 Hours</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {timeRange === 'custom' && (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Start Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customStart ? customStart.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomStart(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">End Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customEnd ? customEnd.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomEnd(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Aggregation</Form.Label>
                <Form.Select size="sm" value={aggregation} onChange={handleAggregationChange}>
                  <option value="minute">Per Minute</option>
                  <option value="hour">Hourly</option>
                  <option value="day">Daily</option>
                  <option value="month">Monthly</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="graph-container">
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <ChartFactory
              chartData={{
                data: treatmentWiseStpData,
                chartType: 'line',
                title: 'Treatment-wise STP Water'
              }}
              chartOptions={{
                height: 350,
                stacked: true,
                transform: true
              }}
              group_by={['displayPeriods','kpi']}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};


export default Company362MeterDashboard;